/**
 * MasterDataServiceFormDialog — Create/Edit `service`.
 * 1:1 port of Access `frmMdQryServiceAdd`. Compact Sales Contract style.
 *
 * Field order (Access): Service Type (combo) → Service Type Name (ro) →
 * Service Code (ro, auto-suggest) → Service Company → Short Name → TLC Pic →
 * Tel → Address → Tax No → Note.
 *
 * service_code auto-suggest theo VBA service_type_AfterUpdate: chọn type →
 * gọi /suggest-code (chỉ create mode). Save-catch: nếu BE báo trùng
 * (race) → tự suggest mã mới + alert vàng (access-suggest anti #13).
 */
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdServiceMutation,
  useLazySuggestServiceCodeQuery,
  useListServiceTypesQuery,
  useUpdateMdServiceMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataService, ServiceTypeOption } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataService | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  service_type: string
  service_type_name: string
  service_code: string
  service_company: string
  short_name: string
  tlc_pic: string
  tel: string
  address: string
  tax_no: string
  note: string
}

const EMPTY: FormState = {
  service_type: '',
  service_type_name: '',
  service_code: '',
  service_company: '',
  short_name: '',
  tlc_pic: '',
  tel: '',
  address: '',
  tax_no: '',
  note: '',
}

export default function MasterDataServiceFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createService, { isLoading: creating }] = useCreateMdServiceMutation()
  const [updateService, { isLoading: updating }] = useUpdateMdServiceMutation()
  const loading = creating || updating

  const { data: serviceTypes = [] } = useListServiceTypesQuery()
  const [triggerSuggest, { isFetching: suggesting }] = useLazySuggestServiceCodeQuery()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    if (isEdit && initialValue) {
      setForm({
        service_type: initialValue.service_type ?? '',
        service_type_name: initialValue.service_type_name ?? '',
        service_code: initialValue.service_code ?? '',
        service_company: initialValue.service_company ?? '',
        short_name: initialValue.short_name ?? '',
        tlc_pic: initialValue.tlc_pic ?? '',
        tel: initialValue.tel ?? '',
        address: initialValue.address ?? '',
        tax_no: initialValue.tax_no ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const selectedType = useMemo<ServiceTypeOption | null>(() => {
    if (!form.service_type) return null
    return (
      serviceTypes.find((t) => t.code === form.service_type) ??
      ({ code: form.service_type, name: form.service_type_name } as ServiceTypeOption)
    )
  }, [serviceTypes, form.service_type, form.service_type_name])
  const typeOpts = useMemo(
    () =>
      selectedType && !serviceTypes.some((o) => o.code === selectedType.code)
        ? [selectedType, ...serviceTypes]
        : serviceTypes,
    [serviceTypes, selectedType],
  )

  // VBA service_type_AfterUpdate: set type name + suggest service_code (create).
  const handleTypeChange = async (opt: ServiceTypeOption | null) => {
    setRegenNotice(null)
    if (!opt) {
      setForm((p) => ({ ...p, service_type: '', service_type_name: '', service_code: isEdit ? p.service_code : '' }))
      return
    }
    setForm((p) => ({ ...p, service_type: opt.code, service_type_name: opt.name ?? '' }))
    if (isEdit) return // edit: service_code immutable
    try {
      const res = await triggerSuggest(opt.code, false).unwrap()
      const code = (res.service_code || '').trim()
      if (code) setForm((p) => ({ ...p, service_code: code }))
    } catch {
      /* BE sẽ raise lúc Save nếu trùng — không poison UX */
    }
  }

  const typeEmpty = !form.service_type.trim()
  const codeEmpty = !form.service_code.trim()
  const canSave = !typeEmpty && !codeEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    service_type: form.service_type.trim(),
    service_company: form.service_company.trim() || null,
    short_name: form.short_name.trim() || null,
    tlc_pic: form.tlc_pic.trim() || null,
    tel: form.tel.trim() || null,
    address: form.address.trim() || null,
    tax_no: form.tax_no.trim() || null,
    note: form.note.trim() || null,
  })

  const handleSave = async () => {
    if (typeEmpty || codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateService({ code: initialValue.service_code, body: buildBody() }).unwrap()
        snack.success('Service updated')
      } else {
        await createService({ service_code: form.service_code.trim(), ...buildBody() }).unwrap()
        snack.success('Service created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      // access-suggest anti #13: duplicate race → auto re-suggest + alert vàng.
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      const lower = msg.toLowerCase()
      const isDup = !isEdit && (lower.includes('exist') || lower.includes('duplicate') || lower.includes('tồn tại'))
      if (isDup) {
        try {
          const res = await triggerSuggest(form.service_type.trim(), false).unwrap()
          const newCode = (res.service_code || '').trim()
          const oldCode = form.service_code.trim()
          if (newCode && newCode !== oldCode) {
            setForm((p) => ({ ...p, service_code: newCode }))
            setRegenNotice({ from: oldCode, to: newCode })
            return
          }
        } catch {
          /* fallthrough → snack error */
        }
      }
      snack.error(msg)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Service' : 'New Service'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.service_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Service Code <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              fullWidth
              options={typeOpts}
              value={selectedType}
              onChange={(_e, v) => void handleTypeChange(v)}
              getOptionLabel={(opt) => (opt ? `${opt.code} — ${opt.name ?? ''}` : '')}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Service Type Code"
                  required
                  error={showErrors && typeEmpty}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, tabIndex: 1 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Service Type Name"
              size="small"
              fullWidth
              value={form.service_type_name}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              inputProps={{ tabIndex: -1 }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Service Code"
              size="small"
              fullWidth
              required
              error={showErrors && codeEmpty}
              value={form.service_code}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                readOnly: true,
                endAdornment: suggesting ? <CircularProgress size={14} /> : undefined,
              }}
              inputProps={{ tabIndex: -1 }}
              helperText={
                isEdit
                  ? 'Service Code cannot be changed after creation.'
                  : suggesting
                    ? 'Suggesting next code…'
                    : 'Auto-generated from Service Type.'
              }
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Service Company"
              size="small"
              fullWidth
              value={form.service_company}
              onChange={(e) => set('service_company', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Short Name"
              size="small"
              fullWidth
              value={form.short_name}
              onChange={(e) => set('short_name', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="TLC Pic"
              size="small"
              fullWidth
              value={form.tlc_pic}
              onChange={(e) => set('tlc_pic', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Tel"
              size="small"
              fullWidth
              value={form.tel}
              onChange={(e) => set('tel', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 5 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              size="small"
              fullWidth
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 6 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Tax No"
              size="small"
              fullWidth
              value={form.tax_no}
              onChange={(e) => set('tax_no', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 7 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 8 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
